import { getAdobeExperimentSSR } from '@cinch-nx/adobe-server-side'
import { getStoryBySlug } from '@cinch-nx/data-storyblok'
import { Env, readFromEnv } from '@cinch-nx/environments'
import { setTag, tagError, traceFunction } from '@core/datadog-server'
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'

import { homeStoryFallback } from './data/fallback/homepage-props-fallback'
import { getVehiclesData } from './data/get-vehicles-data'
import { getPersonalisationEligibility } from './helpers/get-personalisation-eligibility'
import { HomepageVehiclesData } from './types/feature-home-page'
import { HomeStoryContent } from './types/home-story-content'
import { VehicleCondition } from './types/vehicles-data'
import { getHomepageVariantExperienceSlug } from './helpers/get-homepage-variant'

const EXPERIMENT_NAMES = [
  readFromEnv(Env.NavbarValuationExperimentId),
  readFromEnv(Env.AiSearchExperimentId),
]

const HOMEPAGE_STORY_SLUG = 'home'
const HOMEPAGE_VARIANT_B_STORY_SLUG = 'home-redesign-experiment'

async function getStory(preview: boolean, slug = 'home') {
  try {
    const story = await getStoryBySlug<HomeStoryContent>(slug, {
      version: preview ? 'draft' : 'published',
      resolve_relations: 'CarsCarousel.financeRepresentativeExample',
    })
    return { story: story, usingStoryFallback: false }
  } catch (error) {
    tagError(`Error when fetching story '${slug}' from storyblok. ${error}`)
    return { story: homeStoryFallback, usingStoryFallback: true }
  }
}

async function getServerSidePropsfn(ctx: GetServerSidePropsContext) {
  const isInsideStoryblok = !!ctx.preview

  const [
    { vehiclesData: usedVehiclesData, searchResponse },
    { vehiclesData: newVehiclesData },
    { showPersonalisedComponents },
    experiments,
  ] = await Promise.all([
    getVehiclesData({ vehicleCondition: VehicleCondition.Used }),
    getVehiclesData({ vehicleCondition: VehicleCondition.New }),
    getPersonalisationEligibility(ctx.req.cookies),
    getAdobeExperimentSSR(EXPERIMENT_NAMES, ctx),
  ])

  const slug = getHomepageVariantExperienceSlug(
    experiments,
    isInsideStoryblok,
    HOMEPAGE_STORY_SLUG,
    HOMEPAGE_VARIANT_B_STORY_SLUG,
  )
  const { story, usingStoryFallback } = await getStory(isInsideStoryblok, slug)

  setTag('response.code', 200)
  setTag('page.name', 'homepage')
  setTag('page.url', ctx.resolvedUrl)
  setTag('page.rendered', 'SSR')
  setTag(
    'page.params',
    JSON.stringify(
      {
        preview: isInsideStoryblok,
        query: ctx.query,
        showPersonalisedComponents,
      },
      null,
      2,
    ),
  )

  const vehiclesData: HomepageVehiclesData = {
    usedVehicles: {
      ...usedVehiclesData,
      searchResponse,
    },
    newVehicles: newVehiclesData,
  }

  return {
    props: {
      story,
      usingStoryFallback,
      vehiclesData,
      showPersonalisedComponents,
      preview: isInsideStoryblok,
      generatedAt: Date.now().toString(),
      pageMeta: {
        experiments,
      },
    },
  }
}

export const getServerSideProps = traceFunction(
  'homepage.getServerSideProps',
  getServerSidePropsfn,
)

export type HomepageProps = InferGetServerSidePropsType<
  typeof getServerSideProps
>
